import React from "react";
import { AI_IMPORT_PRESETS } from "../utils/formAutomationPresets";

export default function FormAutomationPresetEditor({
  form,
  setForm,
  onCancel,
  onSave,
  saving = false,
  title = "Create company preset",
  saveLabel = "Create preset",
  compact = false,
}) {
  const shellClassName = compact
    ? "rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-900/50 dark:bg-blue-950/20"
    : "rounded-3xl border border-white/10 bg-white/10 p-6 shadow-2xl backdrop-blur-xl";

  const fieldClassName = compact
    ? "w-full rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm text-gray-900 dark:border-blue-800 dark:bg-gray-900 dark:text-gray-100"
    : "w-full rounded-2xl border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-white placeholder:text-white/35 focus:border-cyan-400/50 focus:outline-none";

  return (
    <div className={shellClassName}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className={compact ? "text-sm font-semibold text-blue-900 dark:text-blue-100" : "text-lg font-black text-white"}>
            {title}
          </h3>
          <p className={compact ? "mt-1 text-xs text-blue-800/80 dark:text-blue-200/80" : "mt-1 text-sm text-white/60"}>
            Save reusable AI import patterns with starter sections, field skeletons, and drafting guidance.
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className={compact
            ? "rounded-md border border-blue-200 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 dark:border-blue-800 dark:text-blue-200"
            : "rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-white/80 transition hover:bg-white/10"}
        >
          Close
        </button>
      </div>

      <div className="mt-5 grid gap-4">
        <input
          type="text"
          value={form.name}
          onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
          placeholder="Preset name"
          className={fieldClassName}
        />

        <select
          value={form.basePresetKey}
          onChange={(event) => setForm((current) => ({ ...current, basePresetKey: event.target.value }))}
          className={fieldClassName}
        >
          {AI_IMPORT_PRESETS.map((preset) => (
            <option key={preset.key} value={preset.key}>
              {preset.label}
            </option>
          ))}
        </select>

        <textarea
          value={form.extraInstructions}
          onChange={(event) => setForm((current) => ({ ...current, extraInstructions: event.target.value }))}
          placeholder="Optional extra AI drafting instructions"
          className={`${fieldClassName} min-h-[108px]`}
        />

        <input
          type="text"
          value={form.starterSections}
          onChange={(event) => setForm((current) => ({ ...current, starterSections: event.target.value }))}
          placeholder="Starter sections separated by commas"
          className={fieldClassName}
        />

        <textarea
          value={form.starterFieldTemplates}
          onChange={(event) => setForm((current) => ({ ...current, starterFieldTemplates: event.target.value }))}
          placeholder="Starter field skeletons. Format: Section: field one, field two | Section 2: field three"
          className={`${fieldClassName} min-h-[128px]`}
        />

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className={compact
              ? "rounded-md border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
              : "rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-white/80 transition hover:bg-white/10"}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className={compact
              ? "rounded-md bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              : "rounded-full bg-gradient-to-r from-cyan-400 via-blue-500 to-fuchsia-500 px-5 py-2 text-sm font-bold text-slate-950 shadow-lg transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"}
          >
            {saving ? "Saving..." : saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
